<template>
  <div class="container-fluid py-4 report-container">
    <!-- User dropdown -->
    <div class="mb-4">
      <label for="userSelect" class="font-weight-bold">Select User</label>
      <select
        id="userSelect"
        v-model.number="selectedUserId"
        @change="onUserChange"
        class="form-control"
      >
        <option :value="null">-- Select a User --</option>
        <option v-for="u in usersList" :key="u.id" :value="u.id">
          {{ u.name }} ({{ u.role }}) — {{ u.email }}
        </option>
      </select>

      <div class="d-flex align-items-center mt-2" v-if="selectedUserId">
        <small class="text-muted">
          Selected: {{ selectedUserName || "-" }} • {{ selectedUserRole || "-" }}
        </small>
        <button
          class="btn btn-sm btn-outline-light ml-3"
          @click="fetchReport"
          :disabled="loading"
          title="Refresh report"
        >
          Refresh
        </button>
      </div>
    </div>

    <!-- Report Body -->
    <div v-if="userInfo && userInfo.id" class="report-content">
      <!-- Header -->
      <div class="text-center mb-5 report-header">
        <h2 class="font-weight-bold mb-1">User Analytics Report</h2>
        <div class="text-muted">
          Generated on {{ generatedAt }}
        </div>
      </div>

      <!-- User Info -->
      <section class="mb-4">
        <h4 class="section-title">User Information</h4>
        <div class="row no-gutters align-items-center mb-3">
          <div class="col-auto pr-3" v-if="userAvatar">
            <img :src="userAvatar" alt="User" class="avatar-lg">
          </div>
          <div class="col">
            <table class="table table-bordered table-sm">
              <tbody>
                <tr><th class="w-25">Name</th><td>{{ userInfo.name || "-" }}</td></tr>
                <tr><th>Role</th><td>{{ userInfo.role || "-" }}</td></tr>
                <tr><th>Email</th><td>{{ userInfo.email || "-" }}</td></tr>
                <tr><th>Phone</th><td>{{ userInfo.phone || "-" }}</td></tr>
                <tr><th>Created On</th><td>{{ formatDate(userInfo.createdOn) }}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Summary -->
      <section class="mb-4">
        <h4 class="section-title">Summary</h4>
        <table class="table table-striped table-sm mb-0">
          <thead class="thead-dark">
            <tr>
              <th>Total Followers</th>
              <th>Total Scouts (by Region)</th>
              <th>Followers by Category (Sum)</th>
              <th>Followers by Region (Sum)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ totalFollowersRoot }}</td>
              <td>{{ totalScoutsAllRegions }}</td>
              <td>{{ categoriesTotal }}</td>
              <td>{{ regionFollowersTotal }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Followers Table -->
      <section class="mb-5">
        <div class="d-flex align-items-center justify-content-between">
          <h4 class="section-title mb-0">
            Followers List
            <small class="text-muted">(Total: {{ followers.length }})</small>
          </h4>
          <div class="small text-muted">
            Showing {{ followers.length }} records
          </div>
        </div>
        <div class="table-wrap">
          <table class="table table-striped table-sm">
            <thead class="thead-dark">
              <tr>
                <th>Follower Name</th>
                <th>Scout Name</th>
                <th>Nationality</th>
                <th>Category</th>
                <th>Age</th>
                <th>Position</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(f, i) in followers" :key="i">
                <td>{{ f.followerName }}</td>
                <td>{{ f.scoutName }}</td>
                <td>{{ f.nationality }}</td>
                <td>{{ f.talentCategoryTitle }}</td>
                <td>{{ f.age }}</td>
                <td>{{ f.position }}</td>
              </tr>
              <tr v-if="followers.length === 0">
                <td colspan="6" class="text-center">No data available</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Followers by Category -->
      <section class="mb-5">
        <h4 class="section-title">
          Followers by Category
          <small class="text-muted">(Sum: {{ categoriesTotal }})</small>
        </h4>
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Category</th>
              <th>Total Followers</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(c, i) in categoriesCount" :key="i">
              <td>{{ c.category }}</td>
              <td>{{ numberOrZero(c.totalFollowers) }}</td>
            </tr>
            <tr v-if="categoriesCount.length === 0">
              <td colspan="2" class="text-center">No data available</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Scouts by Region -->
      <section class="mb-5">
        <h4 class="section-title">
          Scouts by Region
          <small class="text-muted">(Sum: {{ totalScoutsAllRegions }})</small>
        </h4>
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Region</th>
              <th>Total Scouts</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in scoutRegionCount" :key="i">
              <td>{{ r.region }}</td>
              <td>{{ numberOrZero(r.totalScouts) }}</td>
            </tr>
            <tr v-if="scoutRegionCount.length === 0">
              <td colspan="2" class="text-center">No data available</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Followers by Region -->
      <section class="mb-5">
        <h4 class="section-title">
          Followers by Region
          <small class="text-muted">(Sum: {{ regionFollowersTotal }})</small>
        </h4>
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Region</th>
              <th>Total Users</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(rg, i) in regionWiseFollowers" :key="i">
              <td>{{ rg.region }}</td>
              <td>{{ numberOrZero(rg.totalUsers) }}</td>
            </tr>
            <tr v-if="regionWiseFollowers.length === 0">
              <td colspan="2" class="text-center">No data available</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Export -->
      <div class="text-center">
        <button class="btn btn-danger" @click="exportFullPDF" :disabled="exporting || loading">
          {{ exporting ? 'Exporting…' : 'Export Full Report to PDF' }}
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!loading && !error" class="empty-state">
      <div class="muted-box">
        Select a user from the dropdown to load the report.
      </div>
    </div>

    <!-- Loading / Error -->
    <div v-if="loading" class="mt-3 text-center">Loading...</div>
    <div v-if="error" class="alert alert-danger mt-3">{{ error }}</div>
  </div>
</template>

<script>
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // IMPORTANT: import the function

export default {
  name: "UserAnalyticsReport",
  data() {
    return {
      // dropdown & selected
      usersList: [],
      selectedUserId: null,
      selectedUserName: "",
      selectedUserRole: "",

      // report data
      userInfo: null,
      followers: [],
      categoriesCount: [],
      scoutRegionCount: [],
      regionWiseFollowers: [],

      // ui state
      loading: false,
      exporting: false,
      error: null,
      generatedAt: new Date().toLocaleString(),

      // root totals holder
      totalFollowersCount:0,
    };
  },
  computed: {
    userAvatar() {
      // If you later fetch profile pic, bind here; for now blank
      return null;
    },
    totalFollowersRoot() {
      return Number(this.totalFollowersCount) || 0;
    },
    totalScoutsAllRegions() {
      return (this.scoutRegionCount || []).reduce(
        (sum, r) => sum + (Number(r.totalScouts) || 0),
        0
      );
    },
    categoriesTotal() {
      return (this.categoriesCount || []).reduce(
        (sum, c) => sum + (Number(c.totalFollowers) || 0),
        0
      );
    },
    regionFollowersTotal() {
      return (this.regionWiseFollowers || []).reduce(
        (sum, r) => sum + (Number(r.totalUsers) || 0),
        0
      );
    },
  },
  methods: {
    numberOrZero(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },
    formatDate(d) {
      if (!d) return "-";
      try {
        return new Date(d).toLocaleString();
      } catch {
        return d;
      }
    },

    async fetchUsersList() {
      this.error = null;
      try {
        const res = await axios.get(
          "https://geotalent-backend-production.up.railway.app/api/Users",
          { headers: { Authorization: "Bearer " + this.$store.state.userData.token } }
        );
        if (res.data && res.data.status === "success") {
          this.usersList = res.data.data || [];
        } else {
          this.error = "Failed to load users list.";
        }
      } catch (e) {
        this.error = e.message || "Failed to load users list.";
      }
    },

    onUserChange() {
      const u = this.usersList.find(x => x.id === this.selectedUserId);
      this.selectedUserName = u ? (u.name || "") : "";
      this.selectedUserRole = u ? (u.role || "") : "";
      this.fetchReport();
    },

    async fetchReport() {
      if (!this.selectedUserId) return;
      this.loading = true;
      this.error = null;
      try {
        // user info
        const userRes = await axios.get(
          `https://geotalent-backend-production.up.railway.app/api/Users/${this.selectedUserId}`,
          { headers: { Authorization: "Bearer " + this.$store.state.userData.token } }
        );
        if (userRes.data && userRes.data.status === "success") {
          this.userInfo = userRes.data.data || {};
          if (!this.selectedUserName) this.selectedUserName = this.userInfo.name || "";
          if (!this.selectedUserRole) this.selectedUserRole = this.userInfo.role || "";
        } else {
          this.userInfo = {};
        }

        // analytics
        const analyticsRes = await axios.get(
          `https://geotalent-backend-production.up.railway.app/api/UserProfile/GetFollowersAnalytics?profileID=${this.selectedUserId}`,
          { headers: { Authorization: "Bearer " + this.$store.state.userData.token } }
        );

        // reset data first
        this.followers = [];
        this.categoriesCount = [];
        this.scoutRegionCount = [];
        this.regionWiseFollowers = [];
        this.totalFollowersCount = 0;

        if (analyticsRes.data && analyticsRes.data.status === "success") {
          const d = analyticsRes.data;
          this.totalFollowersCount = d.totalFollowers || 0;
          this.followers = d.followers || [];
          this.categoriesCount = d.categoriesCount || [];
          this.scoutRegionCount = d.scoutRegionCount || [];
          this.regionWiseFollowers = d.regionWiseFollowers || [];
        } else {
          this.error = "Failed to load analytics.";
        }

        this.generatedAt = new Date().toLocaleString();
      } catch (e) {
        this.error = e.message || "Failed to load report.";
      } finally {
        this.loading = false;
      }
    },

    exportFullPDF() {
      if (!this.userInfo || !this.userInfo.id) {
        alert("Please select a user first!");
        return;
      }
      this.exporting = true;
      try {
        const doc = new jsPDF("p", "pt", "a4");
        const marginX = 40;

        // Title
        doc.setFontSize(16);
        doc.text("User Analytics Report", marginX, 40);
        doc.setFontSize(10);
        doc.text(`Generated on: ${this.generatedAt}`, marginX, 58);
        doc.text(
          `User: ${this.userInfo.name || "-"}  |  Role: ${this.userInfo.role || "-"}`,
          marginX, 72
        );

        // USER INFO
        autoTable(doc, {
          startY: 90,
          theme: "grid",
          head: [["Field", "Value"]],
          body: [
            ["Name", this.userInfo.name || "-"],
            ["Role", this.userInfo.role || "-"],
            ["Email", this.userInfo.email || "-"],
            ["Phone", this.userInfo.phone || "-"],
            ["Created On", this.formatDate(this.userInfo.createdOn)],
          ],
          styles: { fontSize: 9 },
          headStyles: { fillColor: [33, 37, 41] },
        });

        // SUMMARY
        doc.setFontSize(12);
        doc.text("Summary", marginX, doc.lastAutoTable.finalY + 20);
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 30,
          theme: "grid",
          head: [["Total Followers", "Total Scouts", "Followers by Category (Sum)", "Followers by Region (Sum)"]],
          body: [[
            this.totalFollowersRoot,
            this.totalScoutsAllRegions,
            this.categoriesTotal,
            this.regionFollowersTotal
          ]],
          styles: { fontSize: 9 },
          headStyles: { fillColor: [33, 37, 41] },
        });

        // FOLLOWERS LIST
        doc.text("Followers List", marginX, doc.lastAutoTable.finalY + 20);
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 30,
          theme: "grid",
          head: [["Follower Name", "Scout Name", "Nationality", "Category", "Age", "Position"]],
          body: (this.followers || []).map(f => [
            f.followerName || "-",
            f.scoutName || "-",
            f.nationality || "-",
            f.talentCategoryTitle || "-",
            (f.age != null ? f.age : "-"),
            f.position || "-",
          ]),
          styles: { fontSize: 9 },
          headStyles: { fillColor: [33, 37, 41] },
        });

        // FOLLOWERS BY CATEGORY
        doc.text("Followers by Category", marginX, doc.lastAutoTable.finalY + 20);
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 30,
          theme: "grid",
          head: [["Category", "Total Followers"]],
          body: (this.categoriesCount || []).map(c => [
            c.category || "-",
            this.numberOrZero(c.totalFollowers),
          ]),
          styles: { fontSize: 9 },
          headStyles: { fillColor: [33, 37, 41] },
        });

        // SCOUTS BY REGION
        doc.text("Scouts by Region", marginX, doc.lastAutoTable.finalY + 20);
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 30,
          theme: "grid",
          head: [["Region", "Total Scouts"]],
          body: (this.scoutRegionCount || []).map(r => [
            r.region || "-",
            this.numberOrZero(r.totalScouts),
          ]),
          styles: { fontSize: 9 },
          headStyles: { fillColor: [33, 37, 41] },
        });

        // FOLLOWERS BY REGION
        doc.text("Followers by Region", marginX, doc.lastAutoTable.finalY + 20);
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 30,
          theme: "grid",
          head: [["Region", "Total Users"]],
          body: (this.regionWiseFollowers || []).map(rg => [
            rg.region || "-",
            this.numberOrZero(rg.totalUsers),
          ]),
          styles: { fontSize: 9 },
          headStyles: { fillColor: [33, 37, 41] },
        });

        // Save
        doc.save(`user_report_${(this.userInfo.name || "report").replace(/\s+/g, "_")}.pdf`);
      } catch (e) {
        alert("PDF export failed: " + (e.message || e));
      } finally {
        this.exporting = false;
      }
    },
  },
  mounted() {
    this.fetchUsersList();
  },
};
</script>

